import {
  LeadCandidate,
  LeadPipelineStatus,
} from "@inrange/building-manager-api-client";
import React from "react";
import { Badge, Form, Table } from "react-bootstrap";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { DegradedBadge, DuplicateBuildingMark } from "./LeadRowBadges";
import { EnrichmentTierBadge } from "./EnrichmentTierBadge";
import { PipelineStatusLine } from "./PipelineStatusLine";
import { SortHeader } from "./SortHeader";
import { StatusBadge } from "./StatusBadge";
import { WithTooltip } from "../../components/WithTooltip";
import { candidateStatus } from "./candidateStatus";
import { LeadAddressLink } from "./LeadAddressLink";
import {
  buildLandlordTooltip,
  buildTenantTooltip,
  combinedBuildingConfidence,
} from "./leadConfidence";
import {
  OWNER_OCCUPIED_LANDLORD_LABEL,
  distinctOrgNameCount,
  isAutoPickable,
  isLandlordAutoPickable,
} from "./leadDisplay";
import {
  noSelectionOptionsText,
  researchedBlankLandlordStatus,
} from "./leadResearchDisplay";
import { researchRowChipState } from "./leadResearchProgress";
import {
  leadValueBadgeProps,
  leadValueLabel,
  leadValueTooltip,
} from "./leadValueDisplay";
import {
  NO_BUILDING_OVERALL_CAP,
  agenticConfidenceForSelection,
  effectiveTopLandlord,
  filterHmlrToCurrentBuilding,
  landlordConfidenceForSelection,
  locationConfidenceForCandidate,
  overallConfidenceForCandidate,
  savedLandlordIsValid,
  savedTenantIsValid,
  tenantConfidenceForSelection,
} from "./leadCandidateRow";
import { PendingPanelSelection, SortState } from "./leadSearchTableTypes";

// The /leads/search results table. Every column it draws comes from a candidate
// plus the props below - the page keeps the search, the selection and the bulk
// operations - so the table can be read on its own, which the 780 lines of it
// inlined in a 4,300-line component could not.
export const LeadResultsTable: React.FC<{
  // The rows after filtering and sorting.
  visible: LeadCandidate[];
  // How many rows the search returned before the status filter, so an empty
  // table can say which of the two emptied it.
  totalCandidateCount: number;
  // A search has completed at least once. False means "no search yet", which
  // reads differently from "a search matched nothing".
  searchHasCompleted: boolean;
  loading: boolean;
  // The current search could be re-run with a wider radius (a postcode search
  // with a radius set), so the empty state can suggest it.
  canWidenRadius: boolean;
  sort: SortState;
  setSort: (sort: SortState) => void;
  // The row open in the right panel.
  selectedSourceRef: string | null;
  onFocusRow: (sourceRef: string) => void;
  // The rows ticked for a bulk action.
  selectedRowRefs: Set<string>;
  setSelectedRowRefs: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleRow: (sourceRef: string) => void;
  // Rows whose building duplicates a higher-ranked row's, by sourceRef.
  duplicateBuildingByRef: Map<string, LeadCandidate>;
  // The right panel's unsaved edits, so the table tracks a mid-edit change.
  pendingSelectionByRef: Record<string, PendingPanelSelection>;
  pipelineStatusByLeadId: Record<string, LeadPipelineStatus | undefined>;
  // Tags of leads promoted in this browser session, by sourceRef.
  createdLeadTagsByRef: Record<string, string[]>;
  // Tags of rows that were already leads when the search loaded, by lead id.
  tagsByLeadId: Record<string, string[]>;
  // Sites created from a promoted row, by lead id, so the address can link
  // through to one - the same link the /leads inbox draws.
  siteIdsByLeadId: Record<string, string[]>;
  // Landlord-research batch state, for the per-row chip.
  bulkResearchRefs: string[] | undefined;
  bulkResearchCurrent: number;
  bulkResearchCancelledRefs: ReadonlySet<string> | null;
  bulkResearchChainPendingRefs: ReadonlySet<string> | null;
  researchNotRunRefs: ReadonlySet<string> | null;
  bulkResearchActiveSearches: number;
  onRerunResearch: (sourceRef: string) => void;
}> = ({
  visible,
  totalCandidateCount,
  searchHasCompleted,
  loading,
  canWidenRadius,
  sort,
  setSort,
  selectedSourceRef,
  onFocusRow,
  selectedRowRefs,
  setSelectedRowRefs,
  toggleRow,
  duplicateBuildingByRef,
  pendingSelectionByRef,
  pipelineStatusByLeadId,
  createdLeadTagsByRef,
  tagsByLeadId,
  siteIdsByLeadId,
  bulkResearchRefs,
  bulkResearchCurrent,
  bulkResearchCancelledRefs,
  bulkResearchChainPendingRefs,
  researchNotRunRefs,
  bulkResearchActiveSearches,
  onRerunResearch,
}) => (
  <Table size="sm" hover style={{ marginBottom: 0, fontSize: 12 }}>
    <thead style={{ position: "sticky", top: 0, background: "#fff" }}>
      <tr>
        <th style={{ width: 28 }}>
          {(() => {
            // Select-all picks up every visible row. Other
            // bulk actions (Force refresh, Premium enrich)
            // are legitimate on already-Lead rows so we
            // shouldn't preemptively exclude them at the
            // select step. Promote already filters out
            // already-Lead rows itself (via `promotable`)
            // so selecting them is safe - they just get
            // skipped from that one action.
            const selectableRefs = visible.map((c) => c.sourceRef);
            const allSelected =
              selectableRefs.length > 0 &&
              selectableRefs.every((ref) => selectedRowRefs.has(ref));
            const someSelected =
              !allSelected &&
              selectableRefs.some((ref) => selectedRowRefs.has(ref));
            return (
              <Form.Check
                checked={allSelected}
                ref={(el: HTMLInputElement | null) => {
                  // Indeterminate state ("some but not all
                  // selected") isn't a controlled prop in
                  // bootstrap, set it via the DOM ref.
                  if (el) el.indeterminate = someSelected;
                }}
                disabled={selectableRefs.length === 0}
                onChange={() => {
                  if (allSelected) {
                    // Clear only the rows we just selected
                    // (preserves any prior selection on rows
                    // that aren't currently visible - though
                    // in practice the table shows everything).
                    setSelectedRowRefs((prev) => {
                      const next = new Set(prev);
                      for (const ref of selectableRefs) {
                        next.delete(ref);
                      }
                      return next;
                    });
                  } else {
                    setSelectedRowRefs((prev) => {
                      const next = new Set(prev);
                      for (const ref of selectableRefs) {
                        next.add(ref);
                      }
                      return next;
                    });
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                aria-label={
                  allSelected ? "Deselect all rows" : "Select all rows"
                }
              />
            );
          })()}
        </th>
        <SortHeader
          cycle="two-way"
          label="Value"
          sortKey="leadValue"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Conf"
          sortKey="overallConfidence"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Address"
          sortKey="address"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Building (m²)"
          sortKey="surfaceAreaSqm"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Tnt"
          sortKey="tenantConfidence"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Tenant"
          sortKey="tenantName"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Lnd"
          sortKey="landlordConfidence"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Landlord"
          sortKey="landlordName"
          sort={sort}
          setSort={setSort}
        />
        <SortHeader
          cycle="two-way"
          label="Status"
          sortKey="status"
          sort={sort}
          setSort={setSort}
        />
      </tr>
    </thead>
    <tbody>
      {visible.map((c) => {
        const status = candidateStatus(c);
        const isFocused = c.sourceRef === selectedSourceRef;
        const isSelected = selectedRowRefs.has(c.sourceRef);
        return (
          <tr
            key={c.sourceRef}
            onClick={() => onFocusRow(c.sourceRef)}
            className={isFocused ? "table-primary" : undefined}
            style={{
              cursor: "pointer",
              borderLeft: isFocused
                ? "3px solid #0d6efd"
                : "3px solid transparent",
            }}
          >
            <td
              onClick={(e) => {
                // Stop the row-onClick from firing (otherwise
                // selecting a row via its checkbox would also
                // open the right-panel detail view).
                e.stopPropagation();
                if (status === "alreadyLead") return;
                // If the click already hit the input element,
                // its native onChange has fired (or will fire)
                // - skip to avoid a double-toggle. Clicks on
                // the surrounding cell padding fall through
                // to toggleRow here, which is what makes the
                // larger cell-sized click target work.
                if ((e.target as HTMLElement).tagName === "INPUT") {
                  return;
                }
                toggleRow(c.sourceRef);
              }}
              style={{ cursor: "pointer" }}
            >
              <Form.Check
                checked={isSelected}
                onChange={() => toggleRow(c.sourceRef)}
              />
            </td>
            <td>
              <WithTooltip text={leadValueTooltip(c.leadValue)}>
                <Badge
                  {...leadValueBadgeProps(c.leadValue)}
                  style={{ fontSize: 10 }}
                >
                  {leadValueLabel(c.leadValue)}
                </Badge>
              </WithTooltip>
            </td>
            <td>
              {(() => {
                const overall = overallConfidenceForCandidate(c);
                const locationConf = locationConfidenceForCandidate(c);
                const buildingConf = c.matchConfidence ?? null;
                const combinedConf = combinedBuildingConfidence(
                  locationConf,
                  buildingConf
                );
                const landlordTop = effectiveTopLandlord(c);
                const tenantTop = c.tenantMatches[0] ?? null;
                const fmt = (n: number | null): string =>
                  n == null ? "—" : `${Math.round(n)}%`;
                const noBuilding = (c.buildingCandidates?.length ?? 0) === 0;
                const tooltip =
                  overall == null
                    ? "Overall confidence is not reported - we don't have enough signals on this row yet."
                    : [
                        `Overall: ${fmt(overall)} (weakest of building / landlord / tenant)`,
                        `• Building: ${fmt(combinedConf)} (loc ${fmt(locationConf)} + polygon ${fmt(buildingConf)})`,
                        `• Landlord: ${fmt(landlordTop?.confidence ?? null)}`,
                        `• Tenant: ${fmt(tenantTop?.confidence ?? null)}`,
                        ...(noBuilding
                          ? [
                              `(Capped at ${NO_BUILDING_OVERALL_CAP}% - no building polygon was resolved.)`,
                            ]
                          : []),
                      ].join("\n");
                return (
                  <ConfidenceBadge
                    size="compact"
                    confidence={overall}
                    tooltip={tooltip}
                  />
                );
              })()}
            </td>
            <td>
              <div style={{ fontSize: 12 }}>
                <DuplicateBuildingMark
                  firstAddress={
                    duplicateBuildingByRef.get(c.sourceRef)?.address
                  }
                  present={duplicateBuildingByRef.has(c.sourceRef)}
                />
                <LeadAddressLink
                  address={c.address}
                  createdSiteIds={
                    c.existingLeadId
                      ? siteIdsByLeadId[c.existingLeadId]
                      : undefined
                  }
                  matchedExistingSiteID={c.matchedExistingSiteID}
                />
              </div>
            </td>
            <td>
              {(() => {
                // Pending right-panel edit > saved building
                // > top candidate. Mirrors the right-panel
                // logic so the Building cell tracks any
                // mid-edit building change rather than
                // always showing the top default. Renders
                // the building's footprint area + the
                // combined building-match confidence
                // (min(location, building polygon)); the
                // bkey itself is opaque and useless to the
                // operator.
                const pendingBuild = pendingSelectionByRef[c.sourceRef];
                const effectiveBkey =
                  pendingBuild?.buildingBkey ??
                  c.selectedBuildingBkey ??
                  c.buildingCandidates?.[0]?.bkey ??
                  null;
                const effective =
                  c.buildingCandidates?.find((b) => b.bkey === effectiveBkey) ??
                  c.buildingCandidates?.[0];
                const area =
                  effective?.surfaceAreaSqm ?? c.buildingSurfaceAreaSqm;
                if (area == null) {
                  return <span style={{ color: "#aaa" }}>-</span>;
                }
                const isPendingChange =
                  pendingBuild?.dirty &&
                  pendingBuild.buildingBkey != null &&
                  pendingBuild.buildingBkey !==
                    (c.selectedBuildingBkey ?? c.buildingCandidates?.[0]?.bkey);
                return (
                  <>
                    {Math.round(area).toLocaleString()}
                    {isPendingChange && (
                      <span
                        style={{
                          color: "#888",
                          fontSize: 10,
                          marginLeft: 4,
                        }}
                      >
                        unsaved
                      </span>
                    )}
                  </>
                );
              })()}
            </td>
            {(() => {
              // Resolve the tenant cell's contents once
              // and render across two columns: Conf badge
              // and Name. Operator-readable left to right;
              // confidence is sortable, name isn't.
              // Priority: pending right-panel edit > saved
              // selectedTenant > default-pick from
              // tenantMatches.
              const pending = pendingSelectionByRef[c.sourceRef];
              let name: React.ReactNode = (
                <span style={{ color: "#aaa" }}>-</span>
              );
              let conf: number | null = null;
              let confTooltip: string | undefined = undefined;
              let unsaved = false;
              const isOwnerOccupied =
                pending && pending.dirty
                  ? pending.ownerOccupier
                  : (c.ownerOccupier ?? false);
              if (isOwnerOccupied) {
                // OO: one org is both occupier and owner. Show the
                // occupier name here; the Landlord column reads
                // "Owner occupied".
                const ooOrg =
                  (pending && pending.dirty
                    ? (pending.tenant ?? pending.landlord)
                    : (c.selectedTenant ?? c.selectedLandlord)) ?? null;
                if (ooOrg?.displayName != null) {
                  name = ooOrg.displayName;
                  if (pending && pending.dirty) unsaved = true;
                  // Resolve the occupier's confidence from ITS OWN
                  // source list (OO doesn't clear the matches, so the
                  // badge shouldn't blank). The owner org can sit in
                  // the tenant OR the landlord slot, so resolve by
                  // source like the sibling Landlord column - never
                  // fall back to a different company's match. This
                  // agrees with the right panel, which scores the same
                  // org across HMLR + agentic + tenant.
                  if (ooOrg.source === "agentic") {
                    conf = agenticConfidenceForSelection(
                      c.agenticMatches,
                      ooOrg
                    );
                    confTooltip =
                      conf != null ? "Agentic research confidence" : undefined;
                  } else if (ooOrg.source === "hmlr") {
                    conf = landlordConfidenceForSelection(c.hmlrMatches, ooOrg);
                    const match = c.hmlrMatches.find(
                      (m) => m.confidence === conf
                    );
                    confTooltip = buildLandlordTooltip(match ?? null);
                  } else {
                    conf = tenantConfidenceForSelection(c.tenantMatches, ooOrg);
                    const match = c.tenantMatches.find(
                      (m) => m.confidence === conf
                    );
                    confTooltip = buildTenantTooltip(match ?? null);
                  }
                }
              } else if (pending && pending.dirty) {
                if (pending.tenant) {
                  name = pending.tenant.displayName;
                  unsaved = true;
                  // Resolve the unsaved selection's confidence too,
                  // so the column matches the right panel's number
                  // instead of blanking on a mid-edit pick.
                  conf = tenantConfidenceForSelection(
                    c.tenantMatches,
                    pending.tenant
                  );
                  const match = c.tenantMatches.find(
                    (m) => m.confidence === conf
                  );
                  confTooltip = buildTenantTooltip(match ?? null);
                }
              } else if (c.selectedTenant && savedTenantIsValid(c)) {
                conf = tenantConfidenceForSelection(
                  c.tenantMatches,
                  c.selectedTenant
                );
                name = c.selectedTenant.displayName;
                const match = c.tenantMatches.find(
                  (m) => m.confidence === conf
                );
                confTooltip = buildTenantTooltip(match ?? null);
              } else if (c.tenantMatches[0]) {
                conf = c.tenantMatches[0].confidence;
                confTooltip = buildTenantTooltip(c.tenantMatches[0]);
                // Below the auto-pick gate the right panel refuses
                // to pre-select and reads "N options, none
                // selected" - so this cell must not render the name
                // as though it were the row's tenant. The two
                // surfaces have to agree on what IS selected.
                name = isAutoPickable(conf) ? (
                  (c.tenantMatches[0].orgName ?? c.tenantMatches[0].companyName)
                ) : (
                  <span style={{ color: "#aaa" }}>
                    {noSelectionOptionsText(
                      distinctOrgNameCount(
                        c.tenantMatches.map((m) => m.orgName ?? m.companyName)
                      )
                    )}
                  </span>
                );
              }
              return (
                <>
                  <td>
                    <ConfidenceBadge
                      size="compact"
                      confidence={conf}
                      tooltip={confTooltip}
                    />
                  </td>
                  <td>
                    {name}
                    {unsaved && (
                      <span
                        style={{
                          color: "#888",
                          fontSize: 10,
                          marginLeft: 4,
                        }}
                      >
                        unsaved
                      </span>
                    )}
                  </td>
                </>
              );
            })()}
            {(() => {
              const pending = pendingSelectionByRef[c.sourceRef];
              let name: React.ReactNode = (
                <span style={{ color: "#aaa" }}>-</span>
              );
              let conf: number | null = null;
              let confTooltip: string | undefined = undefined;
              let unsaved = false;
              const isOwnerOccupied =
                pending && pending.dirty
                  ? pending.ownerOccupier
                  : (c.ownerOccupier ?? false);
              if (isOwnerOccupied) {
                // OO: the occupier name is in the Tenant column; this
                // column carries the OO status instead.
                name = (
                  <span style={{ color: "#6c757d" }}>
                    {OWNER_OCCUPIED_LANDLORD_LABEL}
                  </span>
                );
              } else if (pending && pending.dirty) {
                if (pending.landlord) {
                  name = pending.landlord.displayName;
                  unsaved = true;
                  // Resolve the unsaved selection's confidence too.
                  // Agentic candidates live in agenticMatches, not
                  // hmlrMatches, so fall back to that list.
                  if (pending.landlord.source === "agentic") {
                    conf = agenticConfidenceForSelection(
                      c.agenticMatches,
                      pending.landlord
                    );
                    confTooltip =
                      conf != null ? "Agentic research confidence" : undefined;
                  } else {
                    conf = landlordConfidenceForSelection(
                      c.hmlrMatches,
                      pending.landlord
                    );
                    const match = c.hmlrMatches.find(
                      (m) => m.confidence === conf
                    );
                    confTooltip = buildLandlordTooltip(match ?? null);
                  }
                }
              } else if (c.selectedLandlord && savedLandlordIsValid(c)) {
                name = c.selectedLandlord.displayName;
                if (c.selectedLandlord.source === "agentic") {
                  conf = agenticConfidenceForSelection(
                    c.agenticMatches,
                    c.selectedLandlord
                  );
                  confTooltip =
                    conf != null ? "Agentic research confidence" : undefined;
                } else {
                  conf = landlordConfidenceForSelection(
                    c.hmlrMatches,
                    c.selectedLandlord
                  );
                  const match = c.hmlrMatches.find(
                    (m) => m.confidence === conf
                  );
                  confTooltip = buildLandlordTooltip(match ?? null);
                }
              } else {
                const filtered = filterHmlrToCurrentBuilding(c);
                const m = filtered[0] ?? null;
                if (m) {
                  conf = m.confidence;
                  confTooltip = buildLandlordTooltip(m);
                  // Same rule as the Tenant cell: below the
                  // auto-pick gate the right panel pre-selects
                  // nothing, so this cell must read "none
                  // selected" rather than naming a deed that
                  // isn't the row's landlord.
                  name = isLandlordAutoPickable(conf) ? (
                    (m.orgName ?? m.proprietorName)
                  ) : (
                    <span style={{ color: "#aaa" }}>
                      {noSelectionOptionsText(
                        distinctOrgNameCount([
                          ...filtered.map((h) => h.orgName ?? h.proprietorName),
                          ...c.agenticMatches.map(
                            (a) => a.orgName ?? a.proprietorName
                          ),
                        ])
                      )}
                    </span>
                  );
                } else {
                  // No saved landlord and no HMLR top-pick. If
                  // landlord research has run, mirror the right-pane
                  // OrgSummary status ("N options, none selected" /
                  // "No options available") instead of a bare "-"
                  // that reads as "research never ran". Options the
                  // right panel would show = filtered HMLR + agentic
                  // (filtered is empty in this branch, so it's the
                  // agentic candidates).
                  const status = researchedBlankLandlordStatus(
                    c,
                    filtered.length + c.agenticMatches.length
                  );
                  if (status != null) {
                    name = <span style={{ color: "#6c757d" }}>{status}</span>;
                  }
                }
              }
              return (
                <>
                  <td>
                    <ConfidenceBadge
                      size="compact"
                      confidence={conf}
                      tooltip={confTooltip}
                    />
                  </td>
                  <td>
                    {name}
                    {unsaved && (
                      <span
                        style={{
                          color: "#888",
                          fontSize: 10,
                          marginLeft: 4,
                        }}
                      >
                        unsaved
                      </span>
                    )}
                    {(() => {
                      // Per-row research state: a single right-panel run, the
                      // in-flight bulk sub-batch, a still-pending chain row, or a
                      // finished chain's unreached ("not run") row - via the
                      // shared helper so /leads matches this exactly.
                      const rs = researchRowChipState({
                        ref: c.sourceRef,
                        bulkRefs: bulkResearchRefs,
                        bulkCurrent: bulkResearchCurrent,
                        bulkCancelledRefs: bulkResearchCancelledRefs,
                        chainPendingRefs: bulkResearchChainPendingRefs,
                        notRunRefs: researchNotRunRefs,
                        serverOperationKey: c.researchOperationKey,
                      });
                      if (rs === "researching") {
                        return (
                          <span
                            style={{
                              color: "#0d6efd",
                              fontSize: 10,
                              marginLeft: 4,
                            }}
                          >
                            researching
                            {bulkResearchActiveSearches > 0
                              ? ` (${bulkResearchActiveSearches})`
                              : ""}
                            …
                          </span>
                        );
                      }
                      if (rs === "queued") {
                        return (
                          <span
                            style={{
                              color: "#888",
                              fontSize: 10,
                              marginLeft: 4,
                            }}
                          >
                            queued…
                          </span>
                        );
                      }
                      if (rs === "not-run") {
                        return (
                          <span
                            style={{
                              color: "#b8860b",
                              fontSize: 10,
                              marginLeft: 4,
                            }}
                          >
                            not run ·{" "}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRerunResearch(c.sourceRef);
                              }}
                              style={{
                                padding: 0,
                                border: "none",
                                background: "none",
                                color: "#0d6efd",
                                fontSize: 10,
                                cursor: "pointer",
                              }}
                            >
                              run again
                            </button>
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </td>
                </>
              );
            })()}
            <td>
              {/* First in the cell: where a promoted row has got to in
                the contacts chain. Same derivation and wording as the
                /leads Status column - the operator promotes here and
                watches it here, so the two must not disagree. On its
                own line because the badges below it are the row's
                settled facts, and the stage is the moving one. */}
              {c.existingLeadId && (
                <div>
                  <PipelineStatusLine
                    status={pipelineStatusByLeadId[c.existingLeadId]}
                    variant="inline"
                  />
                </div>
              )}
              <StatusBadge status={status} />
              {status === "alreadyLead" &&
                (() => {
                  // Tags lookup: prefer the in-session map
                  // (set when this row was promoted in this
                  // browser session); fall back to the
                  // /leads cross-lookup for rows that were
                  // already leads when the search loaded.
                  const sessionTags = createdLeadTagsByRef[c.sourceRef];
                  if (sessionTags && sessionTags.length > 0) {
                    return sessionTags.map((t) => (
                      <Badge
                        key={t}
                        bg="secondary"
                        style={{
                          marginLeft: 4,
                          fontSize: 10,
                          fontWeight: 400,
                        }}
                      >
                        {t}
                      </Badge>
                    ));
                  }
                  const tags = tagsByLeadId[c.existingLeadId ?? ""] ?? [];
                  return tags.map((t) => (
                    <Badge
                      key={t}
                      bg="secondary"
                      style={{
                        marginLeft: 4,
                        fontSize: 10,
                        fontWeight: 400,
                      }}
                    >
                      {t}
                    </Badge>
                  ));
                })()}
              <EnrichmentTierBadge premiumEnrichedAt={c.premiumEnrichedAt} />
              <DegradedBadge sources={c.enrichmentDegradedSources} />
            </td>
          </tr>
        );
      })}
      {visible.length === 0 && (
        <tr>
          <td colSpan={10} style={{ color: "#999", padding: 20 }}>
            {loading ? (
              "Loading search results..."
            ) : !searchHasCompleted ? (
              // No search has completed yet (initial / just cleared).
              "No results yet. Run a search above."
            ) : totalCandidateCount === 0 ? (
              // A search ran and matched nothing - point at the
              // filters that most commonly over-narrow the result.
              <>
                No warehouses matched this search. Try lowering the{" "}
                <strong>Min area (m²)</strong> filter
                {canWidenRadius ? ", widening the radius," : ""} or panning to a
                different area and searching again.
              </>
            ) : (
              // Rows exist but the status filter hides them all.
              "No results match the current status filter."
            )}
          </td>
        </tr>
      )}
    </tbody>
  </Table>
);
